import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useState, startTransition, useEffect } from 'react';
import Process from 'src/resources/images/process.png';
import { ProjectDetailComponent } from 'src/view/components/ProjectDetailComponent';
import { useAppState } from 'src/state-management/state-provider';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import ProjectDialog from 'src/view/components/ProjectDialog';
import { Add, Satellite, FolderOutlined } from '@mui/icons-material';
import FullPageMessage from 'src/view/components/FullPageMessage';
import SiteMapWithCompare from '../DMap/SiteMapWithCompare';
import { countryList } from 'src/constants/countryList';
import { CountryType } from 'src/interface';
import { useNavigate } from 'react-router-dom';
import { Site, UserRole } from 'src/api/Api';
import clientService from 'src/clientService';
import { useSnackbar } from '../components/SnackbarService';
import MediaGallery from '../Admin/Projects/ProjectMedia/MediaGallery';
import { formatDate } from 'src/util/date-utils';
import { ProjectWithSites } from 'src/state-management/state';
import { SampleProjectId, AllowedDataRoomProjectIds } from 'src/enum';

const ProjectDetail = () => {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();

  const { selectedProject } = state;
  const projectDetails = selectedProject?.projectDetails as ProjectWithSites | undefined;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sites, setSites] = useState<Site[]>(projectDetails?.sites || []);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isImageryLoading, setIsImageryLoading] = useState(false);
  const { showSnackbar, snackbarType } = useSnackbar();

  const currentUser = state.user;
  const userRole = currentUser?.role as UserRole[] | undefined;
  const isSampleProject = projectDetails?.project_id === SampleProjectId;

  useEffect(() => {
    const fetchSites = async () => {
      if (!projectDetails?.project_id) return;
      
      if (projectDetails.sites && projectDetails.sites.length > 0) {
        setSites(projectDetails.sites);
        return;
      }

      setIsLoadingSites(true);
      try {
        const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(
          projectDetails.project_id
        );
        const fetchedSites = response.data || [];
        setSites(fetchedSites);
        
        dispatch({
          type: 'SET_PROJECT_SITES',
          payload: fetchedSites,
        });
      } catch (error) {
        console.error('Failed to fetch sites:', error);
        showSnackbar({
          message: 'Failed to load sites for this project',
          type: snackbarType.ERROR,
        });
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
  }, [projectDetails?.project_id, dispatch, showSnackbar, snackbarType.ERROR]);

  // Load satellite imagery for all sites
  useEffect(() => {
    const fetchAllSatelliteImagery = async () => {
      if (sites.length === 0 || !projectDetails?.project_id) return;
      
      // Check if any site needs imagery
      const needsImagery = sites.some(site => !site.satellite_imagery);
      if (!needsImagery) return;

      setIsImageryLoading(true);
      try {
        // Get all site IDs
        const siteIds = sites.map(site => site.site_id);
        
        // Fetch imagery for all sites in one batch
        const response = await clientService.api.getBatchSatelliteImageryApiV1SitesSatelliteImageryBatchPost(
          siteIds
        );
        
        // Create a map of site_id to satellite_imagery
        const imageryMap: Record<string, any> = {};
        response.data?.forEach(item => {
          if (item.site_id && item.satellite_imagery) {
            imageryMap[item.site_id] = item.satellite_imagery;
          }
        });

        // Update sites with imagery
        const updatedSites = sites.map(site => ({
          ...site,
          satellite_imagery: imageryMap[site.site_id] || site.satellite_imagery
        }));

        setSites(updatedSites);
        
        // Update global state
        if (projectDetails) {
          dispatch({
            type: 'SET_PROJECT_DETAILS',
            payload: {
              ...projectDetails,
              sites: updatedSites
            }
          });
        }
      } catch (apiError: any) {
        console.error('Failed to fetch satellite imagery:', apiError);
        const errorMessage = apiError.response?.data?.detail || 
          'Failed to fetch satellite imagery.';
        showSnackbar({ message: errorMessage, type: snackbarType.ERROR });
      } finally {
        setIsImageryLoading(false);
      }
    };

    fetchAllSatelliteImagery();
  }, [sites.length, projectDetails?.project_id, dispatch, showSnackbar, snackbarType.ERROR]);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleAddSiteClick = () => {
    startTransition(() => {
      navigate(`/project/${projectDetails?.project_id}/add-site`);
    });
  };

  const handleManageSitesClick = async () => {
    if (projectDetails?.project_id) {
       try {
         const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(
           projectDetails.project_id
         );
         const fetchedSites = response.data || [];
         dispatch({
           type: 'SET_PROJECT_SITES',
           payload: fetchedSites,
         });
       } catch (error) {
         console.error('Failed to pre-fetch sites:', error);
       }
    }
    startTransition(() => {
      navigate(`/project/${projectDetails?.project_id}/sites`);
    });
  };

  const handleDataRoomClick = () => {
    startTransition(() => {
      navigate(`/project/${projectDetails?.project_id}/data-room`);
    });
  };

  if (!projectDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading project details...</Typography>
      </Box>
    );
  }

  const showUpdateButton = userRole?.includes(UserRole.Admin) || !isSampleProject;

  const showManageSitesButton = userRole?.includes(UserRole.Admin) || 
    userRole?.includes(UserRole.User) || userRole?.includes(UserRole.Employee);

  const showDataRoomButton = projectDetails?.project_id ? AllowedDataRoomProjectIds.includes(projectDetails.project_id) : false;

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mb={3}>
        {showUpdateButton && (
          <Button
            variant="contained"
            endIcon={<CreateOutlinedIcon />}
            color="secondary"
            onClick={handleDialogOpen}
          >
            Update Project Details
          </Button>
        )}

        {showManageSitesButton && (
          <Button
            variant="contained"
            endIcon={<Satellite />}
            color="primary"
            onClick={handleManageSitesClick}
          >
            Manage Sites
          </Button>
        )}

        {showDataRoomButton && (
          <Button
            variant="outlined"
            startIcon={<FolderOutlined />}
            color="primary"
            onClick={handleDataRoomClick}
          >
            Data Room
          </Button>
        )}
      </Box>

      <ProjectDetailComponent
        id={projectDetails.project_id}
        title={projectDetails.project_name}
        subTitle={projectDetails.project_status}
        icon={Process}
        startDateTitle="Credit Period Start"
        startDate={formatDate(projectDetails.project_start_date)}
        endDateTitle="Credit Period End"
        endDate={formatDate(projectDetails.project_end_date)}
        detailTitle={projectDetails.project_developer || ''}
        details={projectDetails.project_description || ''}
        projectType={projectDetails.project_type || ''}
        numberOfSites={sites.length}
        sdg={projectDetails.sdg || []}
        creditPeriod={projectDetails.crediting_period || ''}
        totalArea={projectDetails.total_area || 0}
        hasCarbonModule={projectDetails.has_carbon_module || false}
        hasBiodiversityModule={projectDetails.has_biodiversity_module || false}
        hasEnvModule={projectDetails.has_environmental_module || false}
        areaUnit={projectDetails.area_unit}
        country={countryList.find((option) => option.countryCode2 === projectDetails.country) as CountryType || null}
      >
        {isLoadingSites ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading sites...</Typography>
          </Box>
        ) : sites.length > 0 ? (
          <Box>
            <SiteMapWithCompare
              sites={sites}
              requiredBounrdaryMarker={true}
              fitToBoundary={true}
              isImageryLoading={isImageryLoading}
            />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px" mt={4}>
            <FullPageMessage
              headline="No Sites Available"
              subHeadline="Add a site to your project to get initial analysis and start monitoring"
              ctaText="Add Site"
              onCtaClick={handleAddSiteClick}
              ctaIcon={<Add />}
              showCta={true}
            />
          </Box>
        )}
      </ProjectDetailComponent>

      {projectDetails?.project_id && (
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            Project Media
          </Typography>
          <MediaGallery projectId={projectDetails.project_id} showActions={false} />
        </Box>
      )}

      <ProjectDialog 
        onClose={handleDialogClose} 
        open={isDialogOpen} 
        project={projectDetails}
      />
    </Box>
  );
};

export default ProjectDetail;